"use client";

import { PaginationControls } from "@/components/ui/pagination";
import { ListSkeleton } from "@/components/shared";
import { PublisherGameWishlistList } from "./PublisherGameWishlistList";
import { useListPagination } from "@/hooks";
import type { PublisherGameWishlist } from "../types";
import { publisherGameWishlistSkeletonConfig } from "../config";

interface PublisherGameWishlistListWithPaginationProps {
  initialGames: PublisherGameWishlist[];
  initialPage: number;
  initialTotal: number;
  pageSize: number;
  clanid: number;
  type?: string | null;
}

async function loadPublisherGameWishlist(
  clanid: number,
  page: number,
  pageSize: number,
  type?: string | null,
): Promise<{ items: PublisherGameWishlist[]; total: number }> {
  const { getPublisherGameWishlistClient } =
    await import("../api/getPublisherGameWishlistClient");
  const offset = (page - 1) * pageSize;
  const { games, total } = await getPublisherGameWishlistClient(
    clanid,
    pageSize,
    offset,
    type,
  );
  return { items: games, total };
}

export function PublisherGameWishlistListWithPagination({
  initialGames,
  initialPage,
  initialTotal,
  pageSize,
  clanid,
  type,
}: PublisherGameWishlistListWithPaginationProps) {
  const {
    items: games,
    currentPage,
    pageSize: currentPageSize,
    totalPages,
    isPending,
    handlePageChange,
    handlePageSizeChange,
  } = useListPagination({
    initialItems: initialGames,
    initialPage,
    initialTotal,
    pageSize,
    sortable: false,
    onPageChange: (page, pageSize) =>
      loadPublisherGameWishlist(clanid, page, pageSize, type),
  });

  return (
    <div className="space-y-8">
      {isPending ? (
        <ListSkeleton columns={publisherGameWishlistSkeletonConfig} />
      ) : (
        <PublisherGameWishlistList games={games} />
      )}

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={currentPageSize}
        onPageSizeChange={handlePageSizeChange!}
        disabled={isPending}
      />
    </div>
  );
}
